import type { RefObject } from "react";
import { isFunction } from "lodash";
import { useMutation } from "react-query";
import type { CreateAttemptFormattedPayload } from "@/react/api/quizAttemptsApi";
import { quizAttemptsApi } from "@/react/api/quizAttemptsApi";
import type { ApiError } from "@/react/config/CustomErrors";
import type { CoursesLesson } from "@/react/types/CoursesLesson";
import type { QuizAttempt } from "@/react/types/QuizAttempt";
import { useToast } from "@circle-react-uikit/ToastV2";

interface UseCreateQuizAttemptParams {
  lesson: CoursesLesson;
  onSuccess: (() => void) | null;
  quizPanelRef: RefObject<HTMLDivElement | null>;
  refetchLesson: () => void;
  setLastAttempt: (attempt: QuizAttempt) => void;
}
export const useCreateQuizAttempt = ({
  lesson,
  setLastAttempt,
  refetchLesson,
  quizPanelRef,
  onSuccess,
}: UseCreateQuizAttemptParams) => {
  const { error } = useToast();
  const { mutate, isLoading: isSaving } = useMutation<
    QuizAttempt | null,
    ApiError,
    CreateAttemptFormattedPayload
  >(
    payload => {
      if (!lesson.quiz) return Promise.resolve(null);
      return quizAttemptsApi.create({ quizId: lesson.quiz.id, payload });
    },
    {
      onSuccess: response => {
        if (!response) return;

        setLastAttempt(response);

        if (isFunction(onSuccess)) {
          onSuccess();
        }

        if (quizPanelRef.current) {
          quizPanelRef.current.scrollTop = 0;
        }

        refetchLesson();
      },
      onError: () => {
        error("Error");
      },
    },
  );

  return { mutate, isSaving };
};
